import React, { useEffect, useState } from 'react';
import { PieChart, Pie, Cell, Legend, LineChart, Line, Tooltip, XAxis, YAxis, CartesianGrid } from 'recharts';
import NewAssignmentModal from './components/NewAssignmentModal';
import { Add } from '@mui/icons-material';
import apiService from '../../services/apiService';

const Dashboard = () => {
    const [data, setData] = useState({
        dashboardSummary: [],
        assignmentSummary: [],
        agingBuckets: [],
        assignmentPieData: [],
        agingPieData: [],
        clientsContacted: [],
        promisesToPay: [],
        paymentsCollected: []
    });

    const [assignmentModalOpen, setAssignmentModalOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const tileColours = {
        Assigned: 'cyan-600',
        'Assigned Loans': 'cyan-600',
        Unassigned: 'gray-600',
        'Total Collected': 'red-600',
        'Total Promised': 'orange-600'
    };

    useEffect(() => {
        // Mock data
        const mockresponse = {
            data: {
                dashboardSummary: [
                    { label: "Assigned", value: 3400 },
                    { label: "Unassigned", value: 3400 },
                    { label: "Promised", value: 3400 },
                    { label: "Arrears", value: 35000 },
                    { label: "Collected", value: 35000 }
                ],
                assignmentSummary: [
                    { name: "Unassigned", amount: "58K", loans: 51 },
                    { name: "Abigail Bartley", amount: "35K", loans: 45 },
                    { name: "Ayodele Martin", amount: "23K", loans: 22 },
                    { name: "Kajadia Wong", amount: "28.5K", loans: 31 }
                ],
                agingBuckets: [
                    { range: "Current", amount: "178K", loans: 566 },
                    { range: "1 - 7", amount: "48K", loans: 73 },
                    { range: "8 - 14", amount: "34K", loans: 68 },
                    { range: "15 - 21", amount: "39K", loans: 72 },
                    { range: "22 - 30", amount: "41K", loans: 75 }
                ],
                assignmentPieData: [
                    { name: "Unassigned", value: 58 },
                    { name: "Abigail Bartley", value: 35 },
                    { name: "Ayodele Martin", value: 23 },
                    { name: "Kajadia Wong", value: 28.5 }
                ],
                agingPieData: [
                    { name: "Current", value: 178 },
                    { name: "1 - 7 Days", value: 48 },
                    { name: "8 - 14 Days", value: 34 },
                    { name: "15 - 21 Days", value: 39 },
                    { name: "22 - 30 Days", value: 41 }
                ],
                clientsContacted: [
                    { month: "September", value: 240 },
                    { month: "October", value: 300 },
                    { month: "November", value: 150 }
                ],
                clientsReached: [
                    { month: "September", value: 240 },
                    { month: "October", value: 300 },
                    { month: "November", value: 150 }
                ],
                promisesToPay: [
                    { month: "September", value: 400 },
                    { month: "October", value: 320 },
                    { month: "November", value: 290 }
                ],
                paymentsCollected: [
                    { month: "September", value: 200 },
                    { month: "October", value: 250 },
                    { month: "November", value: 220 }
                ]
            }
        };
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiService.get(
                `${process.env.REACT_APP_API_HOST_COLLECTIONS}/assignment-summary`
            );

            if (response.ok) {
                const result = await response.json();
                setData(result.data);
            } else {
                console.error('Failed to fetch dashboard data');
                setError('Failed to load dashboard data. Please try again.');
            }
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            setError('An error occurred while loading the dashboard. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const COLORS = [
        '#D32F2F',  // Red (Dark Matte)
        '#1976D2',  // Blue (Bright Dark)
        '#388E3C',  // Green (Dark Matte)
        '#F57C00',  // Orange (Bright Dark)
        '#8E24AA',  // Purple (Dark Matte)
        '#0288D1',  // Light Blue (Bright Dark)
        '#C2185B',  // Pink (Dark Matte)
        '#7B1FA2',  // Deep Purple (Dark Matte)
        '#F44336',  // Red (Bright)
        '#FF5722',  // Deep Orange (Bright Dark)
        '#795548',  // Brown (Dark Matte)
        '#009688',  // Teal (Bright Dark)
        '#03A9F4',  // Light Blue (Bright)
        '#FFEB3B',  // Yellow (Bright)
        '#CDDC39',  // Lime (Bright)
        '#8BC34A',  // Light Green (Bright)
        '#9C27B0',  // Purple (Bright Dark)
        '#FF9800',  // Amber (Bright Dark)
        '#2196F3',  // Blue (Bright)
        '#3F51B5',  // Indigo (Dark Matte)
        '#FF4081',  // Pink (Bright)
        '#607D8B',  // Blue Grey (Matte)
        '#4CAF50',  // Green (Bright Dark)
        '#FF9800',  // Orange (Bright)
        '#9E9D24',  // Olive (Matte)
        '#607D8B',  // Slate Grey (Matte)
        '#03A9F4',  // Cyan (Bright)
        '#E91E63',  // Pink (Bright)
        '#0097A7',  // Teal (Bright Dark)
        '#8D6E63',  // Brown (Bright Dark)
        '#03A9F4',  // Cyan (Bright)
        '#D32F2F',  // Red (Bright Dark)
        '#FF5722',  // Orange (Bright Dark)
        '#9E9D24',  // Yellow Green (Matte)
        '#B71C1C',  // Dark Red (Bright Dark)
        '#1976D2',  // Blue (Dark Matte)
        '#FBC02D',  // Amber (Bright Dark)
        '#9E9E9E',  // Grey (Matte)
        '#673AB7',  // Deep Purple (Bright)
        '#FFEB3B',  // Yellow (Bright)
        '#FF9800',  // Orange (Bright Dark)
        '#4CAF50',  // Green (Bright Dark)
        '#8BC34A',  // Light Green (Bright)
    ];

    const chartExplanations = {
        'Clients Contacted': 'Total number of clients contacted in the target month, regardless of the outcome of the contact. If a collections officer does not record their attempt to reach the client, it will not reflect here.',
        'Clients Successfully Reached': 'This represents the total number of clients contacted and successfully reached in the target month.',
        'Promises to Pay': 'This is the total dollar value that was promised in the target month after collections officers make successfuly attempts to contact clients and they promise to pay a sum.',
        'Payments Collected': 'Total amount of payments collected in the target month. This only aggregates payments made on loans that were assigned to a collections officer at the time the payment was made.'
    };

    return (
        <div className="">

            <div className='py-5 px-10'>

                <NewAssignmentModal isOpen={assignmentModalOpen} onClose={() => setAssignmentModalOpen(false)} />

                <h1 className="text-4xl x-font-bold mb-6">Assignment <strong>Dashboard</strong></h1>

                {/* Loading State */}
                {loading && (
                    <div className="flex items-center justify-center py-20">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
                    </div>
                )}

                {/* Error State */}
                {error && !loading && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                        <p className="text-red-600">{error}</p>
                        <button
                            onClick={fetchDashboardData}
                            className="mt-3 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
                        >
                            Retry
                        </button>
                    </div>
                )}

                {/* Main Content - Only show when not loading and no error */}
                {!loading && !error && (
                    <>
                        <span className='border-red-600 text-red-600 hidden'></span>
                        <span className='border-gray-600 text-gray-600 hidden'></span>
                        <span className='border-orange-600 text-orange-600 hidden'></span>
                        <span className='border-cyan-600 text-cyan-600 hidden'></span>

                        <div className='rounded-lg p-10 bg-blue-50'>
                            <h1 className="text-2xl text-gray-700">Summary</h1>
                            <p className='text-xs mt-3 text-gray-500'>Snapshot of a summary of collections activity data, aggregated for the current week.</p>

                            {/* Summary Cards */}
                            <div className="grid grid-cols-4 gap-6 mt-5">
                                {data.dashboardSummary.map((item, index) => (
                                    <div key={index} className={`bg-white bg-opacity-50 border border-${tileColours[item.label] || 'blue-600'} rounded-lg p-4 text-center text-${tileColours[item.label] || 'blue-600'}`}>
                                        <h2 className="text-2xl font-bold">${Math.round(item.value / 1000)}K</h2>
                                        <p className="text-gray-700 uppercase text-sm">{item.label.replace("Assigned Loans", "Assigned")}</p>
                                    </div>
                                ))}
                            </div>

                        </div>
                    </>
                )}

            </div>

            <hr className='my-5' />

            {!loading && !error && (
            <div className='py-5 px-10'>

                {/* Assignment Summary */}
                <h2 className="text-2xl text-gray-700">Assignment Summary</h2>
                <p className='text-xs mt-3 text-gray-500'>Distribution of assigned loans among collections officers, and sum of unassigned loans for the current week.</p>

                <div className="grid grid-cols-3 gap-6 mb-8 mt-5">
                    <div className="col-span-2">
                        <button className='my-3 bg-blue-500 text-white px-5 py-2 rounded-lg' onClick={() => setAssignmentModalOpen(true)}>
                            <Add /> New Assignment
                        </button>
                        <div className="overflow-x-auto">
                            <table className="min-w-full border-collapse border border-gray-200 rounded">
                                <thead className="bg-gray-100">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-sm font-bold text-gray-700 border-b">Name</th>
                                        <th className="px-6 py-3 text-left text-sm font-bold text-gray-700 border-b">Amount</th>
                                        <th className="px-6 py-3 text-left text-sm font-bold text-gray-700 border-b">Loans</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.assignmentSummary.map((item, index) => (
                                        <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-white"}>
                                            <td className="px-6 py-4 text-sm text-gray-700 border-b">{item.name}</td>
                                            <td className="px-6 py-4 text-sm text-gray-700 border-b">{item.amount}</td>
                                            <td className="px-6 py-4 text-sm text-gray-700 border-b">
                                                <a href="/">{item.loans} loans</a>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div>
                        <PieChart width={300} height={300}>
                            <Pie
                                data={data.assignmentPieData}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius={100}
                                label
                            >
                                {data.assignmentPieData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Legend />
                        </PieChart>
                    </div>
                </div>
            </div>
            )}

            {!loading && !error && (
            <>
            <hr className='my-5' />

            <div className='py-5 px-10'>

                {/* Aging Buckets */}
                <h2 className="text-2xl text-gray-700">Aging Buckets</h2>
                <p className='text-xs mt-3 text-gray-500'>Distribution of late loans among aging buckets. This is as of the last update to the dashboard and looks at all late loans.</p>

                <div className="grid grid-cols-3 gap-6 mb-8 mt-8">
                    <div className="col-span-2">
                        <div className="overflow-x-auto">
                            <table className="min-w-full border-collapse border border-gray-200 rounded">
                                <thead className="bg-gray-100">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-sm font-bold text-gray-700 border-b">Range</th>
                                        <th className="px-6 py-3 text-left text-sm font-bold text-gray-700 border-b">Amount</th>
                                        <th className="px-6 py-3 text-left text-sm font-bold text-gray-700 border-b">Loans</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.agingBuckets.map((item, index) => (
                                        <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-white"}>
                                            <td className="px-6 py-4 text-sm text-gray-700 border-b">{item.range}</td>
                                            <td className="px-6 py-4 text-sm text-gray-700 border-b">{item.amount}</td>
                                            <td className="px-6 py-4 text-sm text-blue-500 border-b">
                                                <a href="/">{item.loans} loans</a>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div>
                        <PieChart width={300} height={300}>
                            <Pie
                                data={data.agingPieData}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius={100}
                                label
                            >
                                {data.agingPieData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Legend />
                        </PieChart>
                    </div>
                </div>
            </div>

            <hr className='my-5' />

            <div className='py-5 px-10'>

                {/* Line Charts */}
                <h2 className="text-2xl text-gray-700">Data Trends</h2>
                <p className='text-xs mt-3 text-gray-500 mb-10'>We trend collections activity data on a month-to-month basis. When payments and contacts are made, this data may take a few moments to update.</p>

                <div className="space-y-6">
                    {[
                        { title: "Clients Contacted", dataKey: "value", data: data.clientsContacted },
                        { title: "Clients Successfully Reached", dataKey: "value", data: data.clientsReached },
                        { title: "Promises to Pay", dataKey: "value", data: data.promisesToPay },
                        { title: "Payments Collected", dataKey: "value", data: data.paymentsCollected }
                    ].map((chart, index) => (
                        <div key={index} className='bg-blue-50 bg-opacity-50 rounded-lg mb-10'>
                            <h3 className="px-5 pt-5 text-lg text-blue-800 font-bold mb-2 uppercase">{chart.title}</h3>
                            <p className='px-5 text-sm text-gray-600'>
                                {chartExplanations[chart.title]}
                            </p>
                            <hr className='my-5' />
                            <div className='px-5'>
                                <LineChart width={800} height={300} data={chart.data}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="month" />
                                    <YAxis />
                                    <Tooltip />
                                    {/* <Legend /> */}
                                    <Line type="monotone" dataKey={chart.dataKey} stroke={COLORS[index]} />
                                </LineChart>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            </>
            )}
        </div>
    );
};

export default Dashboard;
